import moment from "moment";
import { mapState } from "vuex";

import { countByKeys } from "@/utils/countByKeys";
import { percentFromNumber } from "@/utils/percentFromNumber";
import { PremiumServiceEventTypeEnum, PremiumServiceEventTypes, PremiumServiceProductTypeEnum, PremiumServiceRequestTypeEnum } from "@/enums/PremiumServiceEventTypes";
import { ServiceStatus } from "@/enums/ServiceStatus";

export default {
  data() {
    return {
      date: {
        start: moment()
          .subtract(1, "week")
          .toISOString()
          .substring(0, 10),
        end: moment()
          .toISOString()
          .substring(0, 10)
      },
      picker: {
        start: false,
        end: false
      },
      allConciergeServices: [],
      eventTypes: Object.values(PremiumServiceEventTypeEnum),
      requestTypes: Object.values(PremiumServiceRequestTypeEnum),
      productTypes: Object.values(PremiumServiceProductTypeEnum),
      loading: true,
      period: "week",
      periods: [
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Week"], value: "week" },
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Month"], value: "month" },
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Year"], value: "year" }
      ],
      headers: [
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Service'], align: "start", value: "serviceName"},
        { text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Type'], value: "serviceItemType" },
        { text: this.$i18n.messages[this.$i18n.locale]['ReportRequests.Cost'], value: "paymentCost" },
        { text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Provider'], value: "providerName" },
        { text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Resident'], value: "residentName" },
        { text: this.$i18n.messages[this.$i18n.locale]['ReportRequests.PaymentStatus'], value: "paymentStatus" },
        { text: this.$i18n.messages[this.$i18n.locale]['ReportRequests.DateBooked'], value: "createdDateTime" }
      ],
      jsonFields: {
        Service: "serviceName",
        Type: "serviceItemType",
        Cost: "paymentCost",
        Provider: "providerName",
        Resident: "residentName",
        "Payment type": "paymentStatus",
        "Date Booked": {
          field: "createdDateTime",
          callback: value => moment(value).format("DD MMM YYYY, HH:MM A")
        }
      }
    };
  },
  async mounted() {
   this.allConciergeServices = (await this.$store.dispatch("getServiceRequests")).filter(x => [
    PremiumServiceEventTypes.AppAccess,
    PremiumServiceEventTypes.ByAppointment,
    PremiumServiceEventTypes.Request,
    PremiumServiceEventTypes.Event,
    PremiumServiceEventTypes.EventBased,
    PremiumServiceEventTypes.ConciergeEvent,
    PremiumServiceEventTypes.Gift,
    PremiumServiceEventTypes.Product
  ].includes(x.serviceItemType))

    await this.$store.dispatch("getAdditionalServiceRequestsByDates", this.date);
    this.loading = false;
  },
  computed: {
    ...mapState({
      facility: (state) => (state as any).facilityModule.selectedFacility,
      residents: state => (state as any).residentModule.residentsShortData,
    }),
    conciergeServicesByDate() {
      const requests = this.$store.state.additionalServicesModule.serviceRequests
        .filter(x => [
          PremiumServiceEventTypes.AppAccess,
          PremiumServiceEventTypes.ByAppointment,
          PremiumServiceEventTypes.Request,
          PremiumServiceEventTypes.Event,
          PremiumServiceEventTypes.EventBased,
          PremiumServiceEventTypes.ConciergeEvent,
          PremiumServiceEventTypes.Gift,
          PremiumServiceEventTypes.Product
        ].includes(x.serviceItemType));

      return requests
    },
    formattedSelectedDateRange() {
      return `${moment(this.date.start).format(this.facility.dateFormat)} - ${moment(this.date.end).format(this.facility.dateFormat)}`;
    },
    formattedStartDate() {
      return moment(this.date.start).format(this.facility.dateFormat)
    },
    formattedEndDate() {
      return moment(this.date.end).format(this.facility.dateFormat)
    },
    formattedDates() {
      return {
        start: moment(this.date.start).format(this.facility.dateFormat),
        end: moment(this.date.end)
      };
    },
    requestsWithServices() {
      const requests = this.conciergeServicesByDate.map(request => {
        //group service types
        if (this.eventTypes.includes(request.serviceItemType)) {
          request.serviceItemType = PremiumServiceEventTypes.Event
        }
        else if (this.requestTypes.includes(request.serviceItemType)) {
          request.serviceItemType = PremiumServiceEventTypes.Request
        }
        else if (this.productTypes.includes(request.serviceItemType)) {
          request.serviceItemType = PremiumServiceEventTypes.Product
        }
        else {
          request.serviceItemType = 'Undefined'
        }
        return request
      });

      return requests
    },
    residentsWithMostsServices() {
      const residentsWithServices = this.residents.map(resident => {
        const requests = this.allConciergeServices.filter(x => x.residentId === resident.id && [ServiceStatus.Completed, ServiceStatus.Approved, ServiceStatus.AwaitingApproval].includes(x.status))

        const item = {
          name: resident?.name || resident?.pname,
          conciergeRequestsCount: requests.length,
          imageUrl: resident?.imageUrl,
        }

        return item;
      }).sort((left, right) => right.conciergeRequestsCount - left.conciergeRequestsCount).slice(0, 6);

      return residentsWithServices;
    },
    servicesByProvider() {
      return countByKeys(this.conciergeServicesByDate, "providerName");
    },
    servicesByCategory() {
      return countByKeys(this.conciergeServicesByDate, "category");
    },
    servicesByChargeType() {
      return countByKeys(this.conciergeServicesByDate, "serviceType");
    },
    serviceRequestWithBookedBy() {
      return this.conciergeServicesByDate.map(request => {
        let bookedBy;

        if (request.bookByFamilyName) {
          bookedBy = this.$t('GeneralTranslations.Family');
        } else if (request.bookByStaffName) {
          bookedBy = this.$t('GeneralTranslations.Staff');
        } else {
          bookedBy = this.$t('GeneralTranslations.Resident');
        }

        return {
          ...request,
          bookedBy
        };
      });
    },
    servicesByBookedBy() {
      return countByKeys(this.serviceRequestWithBookedBy, "bookedBy");
    },
    servicesByStatus() {
      return countByKeys(this.conciergeServicesByDate, "status");
    },
    servicesByDays() {
      const sortedRequest = this.conciergeServicesByDate.sort((left, right) =>
        moment.utc(left.createdDateTime).diff(moment.utc(right.createdDateTime))
      );

      const formattedRequests = sortedRequest.map(request => ({
        ...request,
        createdDateTime: moment(request.createdDateTime).format("DD/MM")
      }));

      return countByKeys(formattedRequests, "createdDateTime");
    },
    requestsByType() {
      return this.conciergeServicesByDate.reduce((result, current) => {
        let costType = current.paymentCost
        if (current.paymentCost > 0) costType = 'Set price'
        else if (current.paymentCost === 'Free') costType = 'Free'
        else if (!current.paymentCost) costType = 'Cost not set'
        else if (current.paymentCost === 'POA') costType = 'POA'

        const date = moment(current.createdDateTime).format("DD/MM");

        const findedDate = result.find(request => request.name === costType);

        if (findedDate) {
          findedDate.data[date] = findedDate.data[date]
            ? findedDate.data[date] + 1
            : 1;
        } else {
          result.push({
            name: costType,
            data: {
              [date]: 1
            }
          });
        }

        return result;
      }, []);
    },
    facilityCurrency() {
      return this.$store.getters.facilityCurrency;
    }
  },
  methods: {
    calculateServicePercentByType(name) {

      const percent = percentFromNumber(
        this.conciergeServicesByDate.length,
        this.servicesByStatus[name]
      );

      return isNaN(percent) ? 0 : percent.toFixed(2);
    },
    async submit() {
      this.loading = true;
      await this.$store.dispatch("getAdditionalServiceRequestsByDates", this.date);
      this.loading = false;
    },
    handleChangePeriod(period) {
      this.date.start = moment()
        .subtract(1, period)
        .toISOString()
        .substring(0, 10);
      this.date.end = moment()
        .toISOString()
        .substring(0, 10);

      this.submit();
    }
  }
};
